(() => {
// Keep component classes out of the shared storefront script scope.
if (customElements.get('cart-edit-modal')) return;

/**
 * ============================================================================
 * 1. CartEditStore (Centralized Reactive State Management)
 * ============================================================================
 */
class CartEditStore extends EventTarget {
  constructor() {
    super();
    this.state = {
      isOpen: false,
      lineItem: null,
      activeHandle: null,
      productData: null,
      masterGroupData: null,
      cartData: null,
      selectedOptions: [],
      selectedVariant: null,
      selectedQuantity: 1
    };
  }

  updateState(newState) {
    this.state = { ...this.state, ...newState };
    this.dispatchEvent(new CustomEvent('state-changed', { detail: this.state }));
  }

  async openEditModal(lineItem) {
    this.updateState({ 
      isOpen: true, 
      lineItem, 
      activeHandle: lineItem.handle,
      productData: null, 
      masterGroupData: null,
      cartData: null,
      selectedVariant: null,
      selectedQuantity: lineItem.quantity 
    });
    
    try {
      const [response, cartResponse] = await Promise.all([
        fetch(`/products/${lineItem.handle}?section_id=cart-edit-product-json`),
        fetch('/cart.js')
      ]);
      const htmlText = await response.text();
      const cartData = cartResponse.ok ? await cartResponse.json() : null;
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlText, 'text/html');
      const jsonScript = doc.querySelector('script[type="application/json"]') || doc.querySelector('.cart-edit-json-payload-wrapper script');
      
      let networkPayload;
      if (jsonScript) {
        networkPayload = JSON.parse(jsonScript.textContent);
      } else {
        const regexMatch = htmlText.match(/\{[\s\S]*\}/);
        if (regexMatch) {
          networkPayload = JSON.parse(regexMatch[0]);
        } else {
          throw new Error("Target JSON schema script tag missing inside section render.");
        }
      }

      const masterGroupData = networkPayload.swatch_group_products || {};
      const currentProductData = masterGroupData[lineItem.handle] || networkPayload;
      const currentVariant = currentProductData.variants?.find(v => String(v.id) === String(lineItem.variantId)) || currentProductData.variants?.[0];

      this.updateState({ 
        masterGroupData,
        cartData,
        productData: currentProductData, 
        selectedVariant: currentVariant,
        selectedOptions: currentVariant ? currentVariant.options : currentProductData.variants?.[0]?.options || [],
        selectedQuantity: this.clampQuantity(lineItem.quantity, {
          ...this.state,
          cartData,
          selectedVariant: currentVariant
        })
      });
    } catch (error) {
      console.error("⛔ CartEditStore Fetch Exception:", error);
    }
  }

  switchProductLocal(newHandle, fallbackVariantId) {
    const masterMap = this.state.masterGroupData;
    if (!masterMap || !masterMap[newHandle]) return;

    const targetProductData = masterMap[newHandle];
    const fallbackVariant = targetProductData.variants.find(v => String(v.id) === String(fallbackVariantId)) || targetProductData.variants[0];
    const targetOptionsArray = targetProductData.options.map((option, index) => {
      const isColorOption = option.name.toLowerCase() === 'color' || option.name.toLowerCase() === 'colour';
      if (isColorOption) {
        return targetProductData.color_name || masterMap[newHandle]?.color_name || fallbackVariant?.options[index] || option.values[0];
      }

      const currentValue = this.state.selectedOptions[index];
      return option.values.includes(currentValue) ? currentValue : fallbackVariant?.options[index] || option.values[0];
    });

    const targetVariant = targetProductData.variants.find(variant =>
      variant.options.every((optionValue, index) => optionValue === targetOptionsArray[index])
    ) || fallbackVariant;

    const nextState = {
      ...this.state,
      activeHandle: newHandle,
      productData: targetProductData,
      selectedVariant: targetVariant,
      selectedOptions: targetVariant ? targetVariant.options : targetOptionsArray
    };
    nextState.selectedQuantity = this.clampQuantity(nextState.selectedQuantity, nextState);

    this.updateState(nextState);
  }

  closeModal() {
    this.updateState({ isOpen: false, lineItem: null, activeHandle: null, productData: null, masterGroupData: null, cartData: null, selectedVariant: null });
  }

  setSelectedOptions(dropdownSelections) {
    const { productData, activeHandle, masterGroupData } = this.state;
    if (!productData) return;

    const targetOptionsArray = productData.options.map((option, index) => {
      const isColorOption = option.name.toLowerCase() === 'color' || option.name.toLowerCase() === 'colour';
      if (isColorOption) {
        const activeColorSwatch = masterGroupData[activeHandle];
        return activeColorSwatch ? activeColorSwatch.color_name : option.values[0];
      }
      return dropdownSelections[index];
    });

    const selectedVariant = productData.variants.find(variant => 
      variant.options.every((optValue, i) => optValue === targetOptionsArray[i])
    );

    const nextState = {
      ...this.state,
      selectedOptions: targetOptionsArray, 
      selectedVariant 
    };
    nextState.selectedQuantity = this.clampQuantity(nextState.selectedQuantity, nextState);

    this.updateState(nextState);
  }

  setQuantity(qty) {
    this.updateState({ selectedQuantity: this.clampQuantity(qty) });
  }

  getMaxQuantityForState(state = this.state) {
    const variant = state.selectedVariant;
    if (!variant) return 1;

    const inventoryQuantity = Number(variant.inventory_quantity);
    const hasFiniteInventory = variant.inventory_policy === 'deny'
      && !!variant.inventory_management
      && Number.isFinite(inventoryQuantity);

    if (!hasFiniteInventory) return null;

    const reservedQuantity = (state.cartData?.items || []).reduce((sum, item) => {
      if (String(item.variant_id) !== String(variant.id)) return sum;
      if (state.lineItem && item.key === state.lineItem.key) return sum;
      return sum + (Number(item.quantity) || 0);
    }, 0);

    return Math.max(0, inventoryQuantity - reservedQuantity);
  }

  clampQuantity(qty, state = this.state) {
    const parsedQuantity = Math.max(1, parseInt(qty, 10) || 1);
    const maxQuantity = this.getMaxQuantityForState(state);
    if (maxQuantity === null) return parsedQuantity;
    return Math.min(parsedQuantity, Math.max(1, maxQuantity));
  }
}
window.CartEditStore = new CartEditStore();


/**
 * ============================================================================
 * 2. <cart-edit-button> Component (Trigger Element)
 * ============================================================================
 */
class CartEditButton extends HTMLElement {
  connectedCallback() {
    // Ajax cart rendering can connect this element before its children exist.
    // Listen on the host so newly inserted Edit controls work as well.
    if (this.handleEditClick) return;
    this.handleEditClick = (e) => {
      e.preventDefault();
      window.CartEditStore.openEditModal({
        key: this.dataset.key,
        variantId: this.dataset.variantId,
        handle: this.dataset.handle,
        quantity: parseInt(this.dataset.quantity, 10)
      });
    };
    this.addEventListener('click', this.handleEditClick);
  }
}
customElements.define('cart-edit-button', CartEditButton);


/**
 * ============================================================================
 * 3. <cart-edit-modal> Component (Top-Down Orchestrator Shell)
 * ============================================================================
 */
class CartEditModal extends HTMLElement {
  connectedCallback() {
    this.innerHTML = `
      <div class="cart-modal-backdrop">
        <div class="cart-modal-content">
          <h2 class="cart-modal-mobile-title">Edit Item</h2>
          <button class="cart-modal-close" type="button">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M0 0L15 15M15 0L0 15" stroke="#111111" stroke-width="1"/>
            </svg>
          </button>
          <div id="modal-container-root"></div>
        </div>
      </div>
      <div class="size-chart-overlay-drawer" id="globalSizeChartDrawer">
        <div class="size-chart-drawer-content">
          <button class="size-chart-drawer-close" type="button" aria-label="Close size chart">
            <svg fill="#000000" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
              <path d="M0 0h24v24H0z" fill="none"></path>
            </svg>
          </button>
          <div id="sizeChartContentPayloadTarget"></div>
        </div>
      </div>
    `;

    this.backdrop = this.querySelector('.cart-modal-backdrop');
    this.closeBtn = this.querySelector('.cart-modal-close');
    this.container = this.querySelector('#modal-container-root');
    this.chartDrawer = this.querySelector('#globalSizeChartDrawer');
    this.chartCloseBtn = this.querySelector('.size-chart-drawer-close');

    this.backdrop.addEventListener('click', (e) => {
      if (e.target === this.backdrop || e.target === this.closeBtn) window.CartEditStore.closeModal();
    });

    this.closeBtn.addEventListener('click', (e) => {
      window.CartEditStore.closeModal();
    });

    this.chartDrawer.addEventListener('click', (e) => {
      if (e.target === this.chartDrawer || e.target.closest('.size-chart-drawer-close')) {
        this.chartDrawer.style.display = 'none';
      }
    });

    window.CartEditStore.addEventListener('state-changed', (e) => this.handleStateChange(e.detail));
  }

  handleStateChange(state) {
    if (state.isOpen) {
      this.querySelector('.cart-modal-content')?.classList.remove('cart-modal-updating');
      this.backdrop.style.display = 'flex';
      
      document.body.classList.add('cart-edit-modal-open');
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      
      if (window.theme && window.theme.Nav && window.theme.Nav().bar) {
        try { window.theme.Nav().bar.hide(true); } catch(err) { console.error(err); }
      }
      
      if (!state.productData) {
        this.container.innerHTML = `<p class="loading-state">Loading</p>`;
      } else {
        if (!this.querySelector('product-gallery')) {
          this.container.innerHTML = `
            <product-gallery></product-gallery>
            <div class="modal-content-column">
              <product-info></product-info>
              <variant-selector></variant-selector>
              <cart-edit-quantity-selector></cart-edit-quantity-selector>
              <update-bag-button></update-bag-button>
            </div>
          `;
        }
        
        this.querySelectorAll('#modal-container-root, .modal-content-column, #modal-container-root > *, .modal-content-column > *').forEach(node => {
          if (node && (typeof node.renderState !== 'undefined' || 'renderState' in node)) {
            node.renderState = state;
          }
        });
      }
    } else {
      this.querySelector('.cart-modal-content')?.classList.remove('cart-modal-updating');
      this.backdrop.style.display = 'none';
      this.chartDrawer.style.display = 'none';
      this.container.innerHTML = '';

      const cartDrawer = document.getElementById('customCartDrawer');
      document.body.style.overflow = cartDrawer && !cartDrawer.classList.contains('hidden') ? 'hidden' : '';
      document.body.style.touchAction = '';
      document.body.classList.remove('cart-edit-modal-open');

      if (window.theme && window.theme.Nav && window.theme.Nav().bar) {
        try { window.theme.Nav().bar.hide(false); } catch(err) { console.error(err); }
      }
    }
  }

  renderSizeChartContent(sizeChartHtml) {
    const contentPayloadContainer = document.getElementById('sizeChartContentPayloadTarget');
    if (!contentPayloadContainer) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(sizeChartHtml || '', 'text/html');
    const existingSizeChart = doc.querySelector('#size_chart');
    const normalizedContent = existingSizeChart
      ? doc.body.innerHTML
      : `<div id="size_chart">${sizeChartHtml || ''}</div>`;

    contentPayloadContainer.innerHTML = `
      <div class="size-chart">
        <div class="size-chart__inner rte">
          <h2>size guide</h2>
          ${normalizedContent}
        </div>
      </div>
    `;

    if (typeof window.convertToTabs === 'function') {
      window.convertToTabs('size_chart');
    }

    if (window.innerWidth <= 768 && typeof window.transposeTables === 'function') {
      window.transposeTables();
    }
  }
}
customElements.define('cart-edit-modal', CartEditModal);


/**
 * ============================================================================
 * 4. Sub-Components Tree Implementation
 * ============================================================================
 */

// <product-gallery> (Clean Scroll Slider - ZOOM LIGHTBOX REMOVED Completely)
class ProductGallery extends HTMLElement {
  set renderState(state) {
    if (this.dataset.activeHandle === state.activeHandle) {
      this.updateWishlistButton(state);
      return;
    }

    const images = state.productData.images || [];
    const hasRenderedGallery = Boolean(this.querySelector('.shopify-product-gallery-snippet'));
    const renderToken = `${state.activeHandle}-${Date.now()}`;

    this.pendingRenderToken = renderToken;
    this.dataset.pendingHandle = state.activeHandle;

    const renderGallery = () => {
      if (this.pendingRenderToken !== renderToken) return;

      this.dataset.activeHandle = state.activeHandle;
      this.dataset.pendingHandle = '';
      this.classList.remove('cart-gallery-preloading');
      this.renderGallery(state, images);
    };

    if (hasRenderedGallery && images[0]) {
      this.classList.add('cart-gallery-preloading');
      this.preloadImage(images[0]).then(renderGallery).catch(renderGallery);
      return;
    }

    renderGallery();
  }

  preloadImage(src) {
    return new Promise((resolve, reject) => {
      const image = new Image();

      image.onload = () => {
        if (typeof image.decode === 'function') {
          image.decode().then(resolve).catch(resolve);
          return;
        }
        resolve();
      };

      image.onerror = reject;
      image.src = src;
    });
  }

  renderGallery(state, images) {
    this.totalSlides = images.length;
    this.currentActiveIndex = 0;

    let sliderSlidesHtml = '';
    if (this.totalSlides > 0) {
      images.forEach((src, idx) => {
        sliderSlidesHtml += `
          <div class="gallery-slide" data-index="${idx}" style="cursor: default;">
            <img src="${src}" alt="Product image frame display" loading="${idx === 0 ? 'eager' : 'lazy'}">
          </div>
        `;
      });
    } else {
      sliderSlidesHtml = `
        <div class="gallery-slide">
          <div style="background: #f4f4f4; width:100%; height:320px; display:flex; align-items:center; justify-content:center; color:#888;">No Image Available</div>
        </div>
      `;
    }

    this.innerHTML = `
      <div class="shopify-product-gallery-snippet" style="position: relative; width: 100%;">
        <div class="gallery-slider" id="productSlider-snippet">
          ${sliderSlidesHtml}
        </div>
        <button
          aria-label="Add to Wishlist"
          data-with-epi="true"
          class="cart-modal-gallery-wishlist unsub-swym-wishlist-collection swym-button swym-heart swym-add-to-wishlist-view-product product_${state.productData.id}"
          data-swaction="addToWishlist"
          data-product-id="${state.productData.id}"
          data-variant-id="${state.selectedVariant?.id || state.productData.representative_variant_id || ''}"
          data-product-url="${window.location.origin}/products/${state.activeHandle}">
          <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg" class="unsub-swym-fill-icon unsub-swym-icon">
            <path d="M5 0.25C7.95556 0.25 9.39815 2.72917 9.75 3.96875C10.1019 2.72917 11.5444 0.25 14.5 0.25C17.4556 0.25 19.25 2.70703 19.25 5.03125C19.25 7.6875 18.1944 9.28125 15.0278 12.4688C11.8541 15.6633 10.1019 16.8958 9.75 17.25C9.39815 16.8958 7.64592 15.6633 4.47222 12.4688C1.30556 9.28125 0.25 7.6875 0.25 5.03125C0.25 2.70703 2.04444 0.25 5 0.25Z" fill="#111111" stroke="#111111" stroke-width="0.5" stroke-linejoin="round"/>
          </svg>
          <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg" class="unsub-swym-bordered-icon unsub-swym-icon">
            <path d="M5 0.25C7.95556 0.25 9.39815 2.72917 9.75 3.96875C10.1019 2.72917 11.5444 0.25 14.5 0.25C17.4556 0.25 19.25 2.70703 19.25 5.03125C19.25 7.6875 18.1944 9.28125 15.0278 12.4688C11.8541 15.6633 10.1019 16.8958 9.75 17.25C9.39815 16.8958 7.64592 15.6633 4.47222 12.4688C1.30556 9.28125 0.25 7.6875 0.25 5.03125C0.25 2.70703 2.04444 0.25 5 0.25Z" fill="white" fill-opacity="0.5" stroke="#111111" stroke-opacity="0.5" stroke-width="0.5" stroke-linejoin="round"/>
          </svg>
        </button>
        
        ${this.totalSlides > 1 ? `
          <button class="gallery-arrow-btn prev-handle" type="button" aria-label="Previous" style="position: absolute; top: 50%; transform: translateY(-50%); left: 12px; display: none; align-items: center; justify-content: center; cursor: pointer; z-index: 10;  padding: 0;">
            <svg width="8" height="14" viewBox="0 0 10 16" fill="none" style="display:block;"><path d="M8 2L2 8L8 14" stroke="#111" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <button class="gallery-arrow-btn next-handle" type="button" aria-label="Next" style="position: absolute; top: 50%; transform: translateY(-50%); right: 12px;   display: block; align-items: center; justify-content: center; cursor: pointer; z-index: 10;  padding: 0;">
            <svg width="8" height="14" viewBox="0 0 10 16" fill="none" style="display:block; "><path d="M2 2L8 8L2 14" stroke="#111" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        ` : ''}

        ${this.totalSlides > 1 ? `
          <div class="gallery-nav-bar">
            <div class="gallery-nav-progress" id="sliderProgress-snippet"></div>
          </div>
        ` : ''}
      </div>
    `;

    this.bindWishlistButton();
    this.updateWishlistButton(state);
    this.initializeSliderEngine();
  }

  updateWishlistButton(state) {
    const wishlistButton = this.querySelector('.cart-modal-gallery-wishlist');
    if (!wishlistButton) return;

    wishlistButton.dataset.productId = state.productData.id;
    wishlistButton.dataset.variantId = state.selectedVariant?.id || state.productData.representative_variant_id || '';
    wishlistButton.dataset.productUrl = `${window.location.origin}/products/${state.activeHandle}`;
    this.syncWishlistState(wishlistButton);
  }

  bindWishlistButton() {
    const wishlistButton = this.querySelector('.cart-modal-gallery-wishlist');
    if (!wishlistButton || wishlistButton.dataset.bound === 'true') return;

    wishlistButton.dataset.bound = 'true';
    wishlistButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.toggleWishlist(wishlistButton);
    });
  }

  getSwymProduct(button) {
    return {
      epi: Number(button.dataset.variantId),
      empi: Number(button.dataset.productId),
      du: button.dataset.productUrl
    };
  }

  withSwym(callback) {
    if (window._swat) {
      callback(window._swat);
      return;
    }

    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(callback);
  }

  syncWishlistState(button) {
    const product = this.getSwymProduct(button);
    button.classList.remove('swym-added');

    this.withSwym((swat) => {
      if (typeof swat.fetchWrtEventTypeET !== 'function') return;

      swat.fetchWrtEventTypeET((items) => {
        const isAdded = (items || []).some(item => {
          const itemProductId = Number(item.empi || item.id);
          const itemVariantId = Number(item.epi || item.vid);
          return itemVariantId === product.epi || itemProductId === product.empi;
        });
        button.classList.toggle('swym-added', isAdded);
        button.dataset.swaction = isAdded ? 'removeFromWishlist' : 'addToWishlist';
      }, swat.EventTypes?.addToWishlist);
    });
  }

  toggleWishlist(button) {
    const product = this.getSwymProduct(button);
    const isAdded = button.classList.contains('swym-added');

    button.disabled = true;

    this.withSwym((swat) => {
      const done = () => {
        button.classList.toggle('swym-added', !isAdded);
        button.dataset.swaction = !isAdded ? 'removeFromWishlist' : 'addToWishlist';
        button.disabled = false;
      };
      const fail = () => {
        button.disabled = false;
      };

      const addToWishlist = swat.addToWishList || swat.addToWishlist;
      const removeFromWishlist = swat.removeFromWishList || swat.removeFromWishlist;

      if (isAdded && typeof removeFromWishlist === 'function') {
        removeFromWishlist.call(swat, product, done, fail);
      } else if (!isAdded && typeof addToWishlist === 'function') {
        addToWishlist.call(swat, product, done, fail);
      } else {
        button.disabled = false;
      }
    });
  }

  initializeSliderEngine() {
    const slider = this.querySelector('#productSlider-snippet');
    const progressLine = this.querySelector('#sliderProgress-snippet');
    const prevArrow = this.querySelector('.gallery-arrow-btn.prev-handle');
    const nextArrow = this.querySelector('.gallery-arrow-btn.next-handle');

    if (!slider) return;

    const toggleArrowVisibility = (index) => {
      if (this.totalSlides <= 1) return;
      if (prevArrow) prevArrow.style.display = (index === 0) ? 'none' : 'flex';
      if (nextArrow) nextArrow.style.display = (index === this.totalSlides - 1) ? 'none' : 'flex';
    };

    const updateProgressBar = (index, targetedBar) => {
      if (this.totalSlides <= 1 || !targetedBar) return;
      const indicatorWidth = 100 / this.totalSlides;
      targetedBar.style.width = indicatorWidth + '%';
      targetedBar.style.transform = `translateX(${index * 100}%)`;
    };

    updateProgressBar(0, progressLine);
    toggleArrowVisibility(0);

    slider.addEventListener("scroll", () => {
      this.currentActiveIndex = Math.round(slider.scrollLeft / slider.clientWidth);
      updateProgressBar(this.currentActiveIndex, progressLine);
      toggleArrowVisibility(this.currentActiveIndex);
    }, { passive: true });

    if (this.totalSlides > 1 && prevArrow && nextArrow) {
      prevArrow.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        if (this.currentActiveIndex > 0) {
          this.currentActiveIndex--;
          slider.scrollTo({ left: this.currentActiveIndex * slider.clientWidth, behavior: 'smooth' });
        }
      });

      nextArrow.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        if (this.currentActiveIndex < this.totalSlides - 1) {
          this.currentActiveIndex++;
          slider.scrollTo({ left: this.currentActiveIndex * slider.clientWidth, behavior: 'smooth' });
        }
      });
    }
  }
}
customElements.define('product-gallery', ProductGallery);

// <product-info>
class ProductInfo extends HTMLElement {
  set renderState(state) {
    const dynamicPrice = state.selectedVariant ? `$${(state.selectedVariant.price / 100).toFixed(2)}` : '';
    const badgeHtml = state.productData.badge_html?.includes('unsub-custom-badge') ? state.productData.badge_html : '';
    const brandName = state.productData.vendor || '';
    this.innerHTML = `
      <div class="info-wrapper" style="margin-bottom: 20px;">
        ${(badgeHtml || brandName) ? `
          <div class="cart-modal-product-meta">
            ${badgeHtml ? `<div class="cart-modal-badge">${badgeHtml}</div>` : ''}
            ${brandName ? `<div class="cart-modal-brand${badgeHtml ? ' cart-modal-brand--after-badge' : ''}">${brandName}</div>` : ''}
          </div>
        ` : ''}
        <h2 class="cart-modal-heading">${state.productData.title}</h2>
        <p class="cart-modal-variant-price" >${dynamicPrice}</p>
        <a class="cart-modal-view-details" href="/products/${state.activeHandle}">View Full Details</a>
      </div>
    `;
  }
}
customElements.define('product-info', ProductInfo);

// <variant-selector>
class VariantSelector extends HTMLElement {
  set renderState(state) {
    console.group("🔍 Telemetry Diagnostics: <variant-selector>");

    if (this.dataset.activeHandle !== state.activeHandle) {
      this.dataset.activeHandle = state.activeHandle;
      this.innerHTML = ''; 
    }

    if (this.innerHTML !== '') {
      const labelSpan = this.querySelector('.swatch-color-text');
      if (labelSpan) labelSpan.innerText = state.productData?.color_name || 'Default';
      
      this.querySelectorAll('.swatch-circle-ring-container').forEach(ring => {
        const btn = ring.querySelector('button');
        const isCurrent = btn.dataset.handle === state.activeHandle;
        ring.style.border = isCurrent ? '1px solid #000' : '1px solid transparent';
      });

      state.productData.options.forEach((option, optionIndex) => {
        const isColorOption = option.name.toLowerCase() === 'color' || option.name.toLowerCase() === 'colour';
        if (isColorOption) return;
        
        const activeSizeValue = state.selectedOptions[optionIndex];
        this.querySelectorAll('.ui-size-node-button').forEach(btn => {
          const isUnavailable = !this.isOptionValueAvailable(state, optionIndex, btn.dataset.value);
          btn.classList.toggle('active-size', btn.dataset.value === activeSizeValue);
          btn.classList.toggle('unavailable-size', isUnavailable);
          btn.setAttribute('aria-disabled', isUnavailable ? 'true' : 'false');
        });
      });

      console.groupEnd();
      return;
    }

    if (!state.productData || !state.masterGroupData) {
      console.groupEnd();
      return;
    }

    const activeColorName = state.productData.color_name || 'Default';

    let html = `
      <div class="swatches-component-wrapper">
        <div class="swatch-header-label" >
          <span class="swatch-heading-label-title">COLOR:</span> <span class="swatch-color-text" >${activeColorName}</span>
        </div>
        <div class="swatches-flex-row" style="display: flex; flex-wrap: wrap; gap: 12px; align-items: center;">
    `;

    Object.keys(state.masterGroupData).forEach(handle => {
      const swatch = state.masterGroupData[handle];
      const isSelected = handle === state.activeHandle;
      const bgImage = swatch.swatch_file_url || swatch.swatch_prod_image_url;

      html += `
        <div class="swatch-circle-ring-container" style="display: flex; align-items: center; justify-content: center; width: 37px; height: 37px; border-radius: 50%; border: ${isSelected ? '1px solid #000' : '1px solid transparent'};">
          <button 
            type="button"
            class="ui-swatch-node-button"
            data-handle="${swatch.handle}"
            data-representative-id="${swatch.representative_variant_id}"
            title="${swatch.color_name}"
            style="
              width: 31px; 
              height: 31px; 
              border-radius: 50%; 
              border: 1px solid rgba(0,0,0,0.08);
              background-color: ${swatch.css_fallback_color || '#eee'};
              background-image: url(${bgImage || ''});
              background-size: cover;
              background-position: center;
              cursor: pointer;
              padding: 0;
              opacity: ${swatch.is_unavailable ? '0.4' : '1'};
            }">
          </button>
        </div>
      `;
    });

    html += `</div></div>`;

    let optionsHtml = '';
    state.productData.options.forEach((option, optionIndex) => {
      const isColorOption = option.name.toLowerCase() === 'color' || option.name.toLowerCase() === 'colour';
      if (isColorOption) return;

      const hasChartData = state.productData.size_chart_html && state.productData.size_chart_html.trim() !== "";

      optionsHtml += `
        <div class="size-selector-component-block" style="margin-bottom: 20px;">
          <div class="size-selector-row-header">
            <span class="size-selector-title">${option.name}</span>
            ${hasChartData ? `<button type="button" class="size-chart-trigger-link">Size Guide</button>` : ''}
          </div>
          <div class="size-buttons-flex-row" data-index="${optionIndex}">
            ${option.values.map(val => {
              const isSelected = state.selectedOptions[optionIndex] === val ? 'active-size' : '';
              const isUnavailable = !this.isOptionValueAvailable(state, optionIndex, val);
              return `
                <button type="button" class="ui-size-node-button ${isSelected} ${isUnavailable ? 'unavailable-size' : ''}" data-value="${val}" aria-disabled="${isUnavailable ? 'true' : 'false'}">
                  ${val}
                </button>
              `;
            }).join('')}
          </div>
        </div>
      `;
    });

    if (optionsHtml !== '') {
      html += `<div class="size-options-wrapper">${optionsHtml}</div>`;
    }

    this.innerHTML = html;

    const buttons = this.querySelectorAll('.ui-swatch-node-button');
    buttons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        window.CartEditStore.switchProductLocal(btn.dataset.handle, btn.dataset.representativeId);
      });
    });

    this.querySelectorAll('.ui-size-node-button').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        const optionIndex = parseInt(btn.parentElement.dataset.index, 10);
        const currentSelections = [...state.selectedOptions];
        currentSelections[optionIndex] = btn.dataset.value;
        window.CartEditStore.setSelectedOptions(currentSelections);
      });
    });

    const chartTrigger = this.querySelector('.size-chart-trigger-link');
    if (chartTrigger) {
      chartTrigger.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        const globalTargetOverlay = document.getElementById('globalSizeChartDrawer');
        const cartEditModal = document.querySelector('cart-edit-modal');
        if (globalTargetOverlay && cartEditModal) {
          cartEditModal.renderSizeChartContent(state.productData.size_chart_html);
          globalTargetOverlay.style.display = 'flex';
        }
      });
    }

    console.groupEnd();
  }

  isOptionValueAvailable(state, optionIndex, value) {
    if (!state.productData?.variants) return false;

    const targetOptions = state.productData.options.map((option, index) => {
      const isColorOption = option.name.toLowerCase() === 'color' || option.name.toLowerCase() === 'colour';
      if (isColorOption) {
        return state.masterGroupData?.[state.activeHandle]?.color_name || state.selectedOptions[index] || option.values[0];
      }

      return index === optionIndex ? value : state.selectedOptions[index];
    });

    const variant = state.productData.variants.find(candidate =>
      candidate.options.every((optionValue, index) => optionValue === targetOptions[index])
    );

    if (!variant || !variant.available) return false;

    const maxQuantity = window.CartEditStore.getMaxQuantityForState({
      ...state,
      selectedVariant: variant
    });

    return maxQuantity === null || maxQuantity > 0;
  }
}
customElements.define('variant-selector', VariantSelector);

// <cart-edit-quantity-selector>
class QuantitySelector extends HTMLElement {
  set renderState(state) {
    const maxQuantity = window.CartEditStore.getMaxQuantityForState(state);

    if (!this.querySelector('.cart-edit-quantity-selector')) {
      this.innerHTML = `
        <div class="cart-edit-quantity-selector" role="group" aria-label="Quantity selector">
          <button class="cart-edit-quantity-button cart-edit-quantity-minus" type="button" aria-label="Decrease quantity">
            <svg width="11" height="1" viewBox="0 0 11 1" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <line y1="0.5" x2="11" y2="0.5" stroke="#111111"/>
            </svg>
          </button>
          <input class="cart-edit-quantity-input" type="number" value="${state.selectedQuantity}" min="1" aria-label="Quantity">
          <button class="cart-edit-quantity-button cart-edit-quantity-plus" type="button" aria-label="Increase quantity">
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M0 5.5H11M5.5 0L5.5 11" stroke="#111111"/>
            </svg>
          </button>
        </div>
      `;

      this.querySelector('.cart-edit-quantity-minus').addEventListener('click', (e) => {
        e.preventDefault();
        window.CartEditStore.setQuantity(window.CartEditStore.state.selectedQuantity - 1);
      });

      this.querySelector('.cart-edit-quantity-plus').addEventListener('click', (e) => {
        e.preventDefault();
        window.CartEditStore.setQuantity(window.CartEditStore.state.selectedQuantity + 1);
      });

      this.querySelector('.cart-edit-quantity-input').addEventListener('input', (e) => {
        const max = window.CartEditStore.getMaxQuantityForState();
        const value = e.target.value;
        if (value === '') return;

        const numericValue = parseInt(value, 10);
        if (Number.isNaN(numericValue)) {
          e.target.value = '';
          return;
        }

        if (max !== null && numericValue > max) {
          window.CartEditStore.setQuantity(max);
          return;
        }

        window.CartEditStore.setQuantity(numericValue);
      });

      this.querySelector('.cart-edit-quantity-input').addEventListener('change', (e) => {
        window.CartEditStore.setQuantity(e.target.value);
      });
    }

    const input = this.querySelector('.cart-edit-quantity-input');
    const minusButton = this.querySelector('.cart-edit-quantity-minus');
    const plusButton = this.querySelector('.cart-edit-quantity-plus');

    input.value = state.selectedQuantity;
    if (maxQuantity === null) {
      input.removeAttribute('max');
    } else {
      input.max = Math.max(1, maxQuantity);
    }

    minusButton.disabled = state.selectedQuantity <= 1;
    plusButton.disabled = maxQuantity !== null && state.selectedQuantity >= maxQuantity;
  }
}
customElements.define('cart-edit-quantity-selector', QuantitySelector);

// <update-bag-button>
class UpdateBagButton extends HTMLElement {
  set renderState(state) {
    const variantExists = !!state.selectedVariant;
    const maxQuantity = window.CartEditStore.getMaxQuantityForState(state);
    const hasStockForSelection = maxQuantity === null || maxQuantity > 0;
    const isAvailable = variantExists && state.selectedVariant.available && hasStockForSelection;
    
    let btnText = 'Update Bag';
    if (!variantExists) btnText = 'Unavailable Selection';
    else if (!isAvailable) btnText = 'Out of Stock';

    this.innerHTML = `
      <button class="update-submit-btn" ${!isAvailable ? 'disabled' : ''}>
        ${btnText}
      </button>
    `;

    const btn = this.querySelector('button');
    if (!isAvailable) {
      btn.disabled = true;
    } else {
      btn.addEventListener('click', () => this.executeShopifyCartUpdate(state));
    }
  }

  async executeShopifyCartUpdate(state) {
    const { lineItem, selectedVariant } = state;
    const selectedQuantity = window.CartEditStore.clampQuantity(state.selectedQuantity, state);
    const btn = this.querySelector('button');
    const sectionRenderPayload = this.getCartSectionRenderPayload();
    const modalContent = this.closest('.cart-modal-content');
    modalContent?.classList.add('cart-modal-updating');
    btn.innerText = "Updating...";
    btn.disabled = true;
    
    try {
      let freshCartPayload = null;

      if (String(selectedVariant.id) !== String(lineItem.variantId)) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineItem.key, quantity: 0 })
        });
        
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: selectedVariant.id,
            quantity: selectedQuantity,
            ...sectionRenderPayload
          })
        });
        
        freshCartPayload = await addResponse.json();

        const cartResponse = await fetch('/cart.js');
        freshCartPayload.cart = await cartResponse.json();
      } else {
        const changeResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: lineItem.key,
            quantity: selectedQuantity,
            ...sectionRenderPayload
          })
        });
        freshCartPayload = await changeResponse.json();
      }
      
      console.log("📡 Shopify mutation synchronized successfully. Intercepted fresh cart data pipeline object:", freshCartPayload);

      const cartRefreshComplete = this.waitForCartRefresh();

      document.dispatchEvent(
        new CustomEvent("theme:cartchanged", {
          bubbles: true,
          cancelable: false,
          detail: {
            ...freshCartPayload,
            cart: freshCartPayload.cart || freshCartPayload,
            scope: 'cart-summary'
          }
        })
      );

      await cartRefreshComplete;
      const cartDrawer = document.getElementById('customCartDrawer');
      if (cartDrawer && !cartDrawer.classList.contains('hidden') && typeof window.openCustomDrawer === 'function') {
        await window.openCustomDrawer();
      }
      window.CartEditStore.closeModal();
    } catch (err) {
      console.error("⛔ Shopify AJAX Integration Error:", err);
      modalContent?.classList.remove('cart-modal-updating');
      btn.innerText = "Error Updating";
      btn.disabled = false;
    }
  }

  getCartSectionRenderPayload() {
    const cartForm = document.querySelector('cart-form[data-ajax-update="true"]');
    const sectionId = cartForm?.dataset.sectionId;

    if (!sectionId) return {};

    return {
      sections: [sectionId],
      sections_url: window.location.pathname
    };
  }

  waitForCartRefresh() {
    const cartForm = document.querySelector('cart-form[data-ajax-update="true"]');

    if (!cartForm) {
      return new Promise((resolve) => setTimeout(resolve, 500));
    }

    return new Promise((resolve) => {
      let hasStartedRefreshing = cartForm.classList.contains('cart-form--refreshing');
      let isResolved = false;

      const finish = () => {
        if (isResolved) return;
        isResolved = true;
        observer.disconnect();
        clearTimeout(fallbackTimeout);
        resolve();
      };

      const observer = new MutationObserver(() => {
        if (cartForm.classList.contains('cart-form--refreshing')) {
          hasStartedRefreshing = true;
          return;
        }

        if (hasStartedRefreshing) finish();
      });

      const fallbackTimeout = setTimeout(finish, 1500);

      observer.observe(cartForm, { attributes: true, attributeFilter: ['class'] });

      requestAnimationFrame(() => {
        if (!hasStartedRefreshing && !cartForm.classList.contains('cart-form--refreshing')) {
          setTimeout(finish, 250);
        }
      });
    });
  }
}
customElements.define('update-bag-button', UpdateBagButton);
})();
